/**
 * MarketRowUtils.js
 *
 * 경기 카드를 "마켓별 행"으로 펼쳐 표시(blk-111 참고 패턴)하기 위한 순수 함수 모음.
 * 백엔드 user-api `transformMarkets()` 가 이미 line 별로 market 을 그룹핑해
 *   marketName 에 "핸디캡 -1.5" / "오버/언더 5.5" 와 같이 line 을 suffix 로 포함시킨다.
 * 본 모듈은 그 market 객체 한 개를 받아 화면 1행을 구성하는 데 필요한 표시 정보로
 * 변환한다. React·DOM 의존 없음, 테스트 용이, 재사용 용이.
 *
 * 설계 원칙:
 * - market.marketType 이 이미 백엔드에서 'match_result' | 'handicap' | 'over_under' | 'other'
 *   로 분류되어 오므로 그 값만 믿고 분기 (하드코딩 최소화).
 * - outcome.name 은 백엔드 `getOutcomeName()` 에서 '승'/'무'/'패'/'오버'/'언더' 로
 *   한글화된 값이 옴. 그 외 이름(정확한 스코어 등)은 그대로 원본 노출.
 * - 중앙 컬럼 값(라인/토탈/무배당/VS)은 마켓 타입에 따라 달라진다:
 *     · match_result + 3outcomes → 중앙 = 무배당 숫자 (가중치 앵커)
 *     · match_result + 2outcomes → 중앙 = 'VS' (장식)
 *     · handicap / over_under      → 중앙 = line 값 (marketName 에서 추출)
 *     · 그 외                      → 중앙 = 'VS' (fallback)
 */

/**
 * marketName 에서 "기본 마켓명" 과 "라인 suffix" 를 분리.
 * 백엔드 포맷:
 *   - "승무패"           → baseName: "승무패",          line: null
 *   - "핸디캡 -1.5"       → baseName: "핸디캡",          line: "-1.5"
 *   - "오버/언더 5.5"     → baseName: "오버/언더",       line: "5.5"
 *   - "전반 승무패"       → baseName: "전반 승무패",     line: null
 *
 * 정규식: 마지막 공백 뒤에 [부호?숫자.숫자] 또는 [부호?숫자] 형태가 오면 line 으로 인식.
 * line 이 없는 마켓은 그대로 baseName 으로 반환.
 */
export function parseMarketName(marketName) {
  if (!marketName || typeof marketName !== 'string') {
    return { baseName: '', line: null };
  }
  // 끝에서 공백 + (옵션 부호)(숫자).(옵션 소수점 숫자) 패턴 매칭
  const m = marketName.match(/^(.+?)\s+([+\-]?\d+(?:\.\d+)?)\s*$/);
  if (m) {
    return { baseName: m[1].trim(), line: m[2] };
  }
  return { baseName: marketName.trim(), line: null };
}

/**
 * outcome 배열에서 이름 기반으로 home/draw/away/over/under 를 찾는다.
 * 인덱스가 아니라 name 기반이라 순서 변경에 안전.
 */
function findOutcomesByName(outcomes) {
  const by = (names) => outcomes.find(o => names.includes(o.name));
  return {
    home: by(['승', 'Home', '1']),
    draw: by(['무', 'Draw', 'X']),
    away: by(['패', 'Away', '2']),
    over: by(['오버', 'Over']),
    under: by(['언더', 'Under']),
  };
}

/**
 * 마켓 1개를 1행 표시 정보로 변환.
 *
 * 반환 구조:
 *   {
 *     marketType,        // 'match_result' | 'handicap' | 'over_under' | 'other'
 *     homeLabel,         // 홈쪽에 표시할 마켓 라벨 (예: '승무패', '핸디', '오버')
 *     awayLabel,         // 원정쪽에 표시할 마켓 라벨 (오버/언더는 '언더')
 *     homeBadge,         // 홈 배당 옆 작은 라벨 (예: 'HA','AH','OVER')
 *     awayBadge,         // 원정 배당 옆 작은 라벨
 *     homeOutcome,       // 홈 쪽 outcome 객체 (클릭 이벤트용)
 *     awayOutcome,       // 원정 쪽 outcome 객체
 *     centerValue,       // 중앙 컬럼에 표시할 문자열
 *     extraOutcomes,     // home/away 외 추가 outcome (예: 정확한 스코어)
 *   }
 *
 * 호출측에서 이 정보를 사용해 실제 JSX 를 그린다.
 */
export function marketToRow(market) {
  if (!market || !market.outcomes) {
    return null;
  }
  const outcomes = market.outcomes;
  const type = market.marketType || 'other';
  const { baseName, line } = parseMarketName(market.marketName || '');
  const picked = findOutcomesByName(outcomes);

  // 1) 승무패 / 승패 — match_result
  if (type === 'match_result') {
    const hasDraw = !!picked.draw;
    if (hasDraw && picked.home && picked.away) {
      // 3-way (축구 등): 중앙 = 무 배당 숫자
      return {
        marketType: type,
        homeLabel: baseName, awayLabel: baseName,
        homeBadge: 'HA', awayBadge: 'HA',
        homeOutcome: picked.home,
        awayOutcome: picked.away,
        // 무 배당은 별도 outcome 으로 클릭 가능해야 한다.
        centerOutcome: picked.draw,
        centerValue: picked.draw.odds?.toFixed(2) ?? '-',
        extraOutcomes: [],
      };
    }
    // 2-way (야구 등): 중앙 = 'VS' 고정, 무 배당 없음
    if (picked.home && picked.away) {
      return {
        marketType: type,
        homeLabel: baseName, awayLabel: baseName,
        homeBadge: 'HA', awayBadge: 'HA',
        homeOutcome: picked.home,
        awayOutcome: picked.away,
        centerOutcome: null,
        centerValue: 'VS',
        extraOutcomes: [],
      };
    }
  }

  // 2) 핸디캡 — handicap
  if (type === 'handicap') {
    // 3-Way 핸디캡은 무(0) outcome 이 있을 수 있음. 표준 2way 기준으로 처리하고
    // draw 가 있으면 extra 로 분리해 확장 여지를 둔다.
    if (picked.home && picked.away) {
      return {
        marketType: type,
        homeLabel: baseName, awayLabel: baseName,
        homeBadge: 'AH', awayBadge: 'AH',
        homeOutcome: picked.home,
        awayOutcome: picked.away,
        centerOutcome: picked.draw || null,
        centerValue: line || 'VS',
        extraOutcomes: [],
      };
    }
  }

  // 3) 오버/언더 — over_under
  if (type === 'over_under') {
    if (picked.over && picked.under) {
      return {
        marketType: type,
        homeLabel: '오버', awayLabel: '언더',
        // [2026-04-21] OVER/UNDE 텍스트 배지 대신 ▲/▼ 삼각형 아이콘으로 시각화하기 위해
        //   전용 마커 값으로 교체. 실제 아이콘 렌더 및 색(빨강/파랑)·변동 애니메이션은
        //   SportsMatchList.renderOddsButton + SportsPage.css(.matchrow__odds-badge--tri-*) 에서 처리.
        homeBadge: 'OVER_ARROW', awayBadge: 'UNDER_ARROW',
        homeOutcome: picked.over,
        awayOutcome: picked.under,
        centerOutcome: null,
        centerValue: line || '-',
        extraOutcomes: [],
      };
    }
  }

  // 4) 그 외(특수 마켓 — 정확한 스코어, 양팀득점 등)
  //    첫 outcome 을 홈, 두 번째를 원정 자리에 배치하고 3개째부터는 extra 로.
  // [2026-10-18 W93] 다만 승·무·패 이름이 있으면 이름으로 칸을 놓는다(전반 승무패·60분 승무패 같은 마켓).
  //   WHY: 순서로 놓으면 [승, 무, 패] 마켓의 '무'가 원정 칸에 앉고 '패'는 보이지 않았다(extra 는 그리지 않는다).
  //   손등록 경기의 승무패-60분 등이 이 갈래로 온다(서버가 승·무·패 이름과 순서를 붙여 내려준다). 이름이 없으면 종전 그대로.
  //   홈·원정 중 한쪽이 빠진 승무패형(허용 범위 밖이라 서버가 숨긴 배당)은 그리지 않는다 — 남은 칸이 순서대로 앉으면
  //   원정 칸에 '무'가 보이는 식으로 회원이 고른 칸과 다른 쪽에 베팅된다.
  // [2026-10-19 W93] 이 갈래는 선택지가 전부 승·무·패 이름인 마켓(승무패형)만 탄다.
  //   WHY(독립 검토 지적 8): 승·무·패 이름이 하나라도 있으면 이 갈래를 타게 해, 자동 경기의 '그 밖' 마켓 중 이름 하나만
  //   무·1·2 등으로 바뀌는 마켓(예: 무승부 선택지만 있는 '최다 득점 하프')이 통째로 숨었다 — 종전처럼 순서대로 놓는다.
  const oneXTwoNamed = [picked.home, picked.draw, picked.away].filter(Boolean);
  if (oneXTwoNamed.length > 0 && oneXTwoNamed.length === outcomes.length) {
    if (!(picked.home && picked.away)) return null;
    return {
      marketType: type,
      homeLabel: baseName, awayLabel: baseName,
      homeBadge: null, awayBadge: null,
      homeOutcome: picked.home,
      awayOutcome: picked.away,
      centerOutcome: picked.draw || null,
      centerValue: picked.draw ? (picked.draw.odds?.toFixed(2) ?? '-') : (line || 'VS'),
      extraOutcomes: outcomes.filter(o => o !== picked.home && o !== picked.away && o !== picked.draw),
    };
  }
  if (outcomes.length >= 2) {
    return {
      marketType: type,
      homeLabel: baseName, awayLabel: baseName,
      homeBadge: null, awayBadge: null,
      homeOutcome: outcomes[0],
      awayOutcome: outcomes[1],
      centerOutcome: null,
      centerValue: line || 'VS',
      extraOutcomes: outcomes.slice(2),
    };
  }

  // 5) outcome 1개 이하인 비정상 마켓은 null 반환 (호출측에서 skip)
  return null;
}

/**
 * match 객체에서 "표시할 마켓 목록" 을 구성.
 * - 펼치기 전(isExpanded=false) → 메인 마켓 1개만
 * - 펼친 상태(isExpanded=true)   → 캐시된 상세 마켓 전체 (메인 포함)
 * 호출측에서 marketToRow() 를 각 항목에 적용해 행을 렌더한다.
 *
 * @param {object} match       — adaptFixtureTable 응답 구조
 * @param {Array}  cachedMarkets — detailMarketsCache[match.matchId] (null 가능)
 * @param {boolean} isExpanded — 이 경기를 펼친 상태인지
 */
export function pickMarketsForRows(match, cachedMarkets, isExpanded) {
  // 메인 마켓은 match.markets 첫 원소(buildMainMarket) 에 존재
  const mainMarket = Array.isArray(match?.markets) && match.markets.length > 0
    ? match.markets[0]
    : null;

  if (!isExpanded || !cachedMarkets || cachedMarkets.length === 0) {
    // 펼침 전 또는 상세 미로드 → 메인만
    return mainMarket ? [mainMarket] : [];
  }

  // 펼침 + 상세 로드 완료 → 상세 전체. 메인 마켓이 상세에도 있으면 중복 제거.
  const mainId = mainMarket?.marketId;
  const seen = new Set();
  const result = [];
  // 메인 마켓은 항상 첫 번째로 유지 (사용자 기대치)
  if (mainMarket) {
    result.push(mainMarket);
    seen.add(mainId);
  }
  for (const m of cachedMarkets) {
    if (!m?.marketId || seen.has(m.marketId)) continue;
    seen.add(m.marketId);
    result.push(m);
  }
  return result;
}
